import { useEffect, useLayoutEffect, useRef } from "react";

import {
  barPush,
  type Lens,
  lensAt,
  lensShape,
  nearness,
  rubber,
  settled,
  slotAt,
  step,
} from "../lib/liquidTabs";
import { haptic } from "../lib/telegram";

export type Tab = "home" | "history" | "workout" | "programs" | "profile";

interface Props {
  active: Tab;
  workoutActive: boolean;
  onChange: (tab: Tab) => void;
  /**
   * Тап по уже открытой вкладке. Как в нативных приложениях: закрыть то, что
   * открыто поверх, и вернуться в начало раздела (21-retention-plan.md §2).
   */
  onReselect?: (tab: Tab) => void;
}

/** Иконки рисуем сами: внешние шрифты и спрайты в Mini App — лишний запрос. */
const ICONS: Record<Tab, JSX.Element> = {
  programs: (
    <>
      <rect x="3" y="4" width="18" height="4" rx="1.5" />
      <rect x="3" y="10" width="18" height="4" rx="1.5" />
      <rect x="3" y="16" width="18" height="4" rx="1.5" />
    </>
  ),
  history: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </>
  ),
  workout: (
    <>
      <path d="M12 6v12M6 12h12" />
    </>
  ),
  home: (
    <>
      <path d="M4 11l8-6.5 8 6.5" />
      <path d="M6 10v9h12v-9" />
    </>
  ),
  profile: (
    <>
      <circle cx="12" cy="8.5" r="3.5" />
      <path d="M5 20c1.2-3.6 4-5.5 7-5.5s5.8 1.9 7 5.5" />
    </>
  ),
};

const LABELS: Record<Tab, string> = {
  home: "Главная",
  history: "История",
  workout: "Тренировка",
  programs: "Программы",
  profile: "Профиль",
};

// Библиотеку упражнений вытеснила главная: в справочник ходят реже, чем в
// программы, и он остался строкой в профиле.
const ORDER: Tab[] = ["home", "history", "workout", "programs", "profile"];

/** Поля панели до крайних вкладок, px. Совпадает с --pad в CSS. */
const INSET = 16;
/** Радиус выреза под кругом, px. Совпадает с --notch в CSS. */
const NOTCH = 32;
/** На сколько поднимается иконка — ровно в центр круга. */
const RISE = 32;
/** Сколько пикселей пальцем считается перетаскиванием, а не тапом. */
const DRAG_FROM = 6;

/**
 * Нижняя навигация в духе «liquid navigation» (референсы заказчика, 14.09.2026).
 *
 * Синий круг стоит над активной вкладкой, под ним в панели круглый вырез.
 * Круг живой: на нажатие набухает, в пути вытягивается каплей — и вырез
 * тянется вместе с ним, — приезжает с перелётом, у края толкает панель. Его
 * можно вести пальцем: иконка под ним поднимается, вкладка выбирается, когда
 * палец отпущен.
 *
 * Физика — lib/liquidTabs.ts. Здесь цикл requestAnimationFrame, который пишет
 * transform и переменные выреза прямо в DOM: React на кадр не перерисовывается.
 * Пока круг стоит, кадров нет вовсе.
 */
export default function TabBar({ active, workoutActive, onChange, onReselect }: Props) {
  const index = Math.max(0, ORDER.indexOf(active));
  const barRef = useRef<HTMLDivElement>(null);
  const lensRef = useRef<HTMLSpanElement>(null);
  const iconRefs = useRef<(HTMLSpanElement | null)[]>([]);

  // Состояние физики живёт в ref: оно меняется 60 раз в секунду.
  const lens = useRef<Lens>(lensAt(-1));
  const loop = useRef({ slot: 0, raf: 0, last: 0, still: false });
  const drag = useRef<{ id: number; startX: number; active: boolean; slot: number } | null>(null);
  const swallowClick = useRef(false);
  // Замыкания ResizeObserver и жестов живут дольше рендера — текущее берут отсюда.
  const latest = useRef({ index, active, onChange, onReselect });
  latest.current = { index, active, onChange, onReselect };

  const center = (i: number) => INSET + loop.current.slot * (i + 0.5);

  /** Один кадр: круг, вырез под ним, панель и иконки. */
  const paint = () => {
    const s = lens.current;
    const { slot } = loop.current;
    const bar = barRef.current;
    const el = lensRef.current;
    if (!bar || !el || !slot) return;

    const { sx, sy } = lensShape(s);
    el.style.transform = `translate3d(${s.x}px,0,0) translate(-50%,-50%) scale(${sx},${sy})`;
    // Вырез повторяет форму капли: круг тянется — тянется и дыра под ним.
    bar.style.setProperty("--notch-x", `${s.x}px`);
    bar.style.setProperty("--notch-rx", `${NOTCH * sx}px`);
    bar.style.setProperty("--notch-ry", `${NOTCH * sy}px`);
    const push = barPush(s.x, center(0), center(ORDER.length - 1));
    bar.style.transform = `translate3d(${push}px,0,0) scale(${1 + 0.015 * s.press})`;

    iconRefs.current.forEach((icon, i) => {
      if (!icon) return;
      const near = nearness(center(i), s.x, slot);
      icon.style.transform = `translateY(${-RISE * near}px) scale(${1 + 0.1 * s.press * near})`;
      icon.classList.toggle("tab__icon--lifted", near > 0.5);
    });
  };

  const tick = (ms: number) => {
    const l = loop.current;
    const s = lens.current;
    step(s, (ms - (l.last || ms)) / 1000);
    l.last = ms;
    if (settled(s) && !drag.current) {
      s.x = s.target;
      s.v = 0;
      s.press = s.pressTarget;
      s.pressV = 0;
      l.raf = 0;
      l.last = 0;
      paint();
      return;
    }
    paint();
    l.raf = requestAnimationFrame(tick);
  };

  const kick = () => {
    const l = loop.current;
    const s = lens.current;
    if (l.still) {
      s.x = s.target;
      s.press = 0;
      paint();
      return;
    }
    if (!l.raf) l.raf = requestAnimationFrame(tick);
  };

  // Геометрия: ширина вкладки зависит от ширины экрана.
  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const l = loop.current;
    l.still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const measure = () => {
      const s = lens.current;
      const first = !l.slot;
      l.slot = (bar.clientWidth - 2 * INSET) / ORDER.length;
      s.target = center(latest.current.index);
      // Первый замер и смена ширины ставят круг на место без анимации.
      if (first || !l.raf) {
        s.x = s.target;
        s.v = 0;
      }
      paint();
    };
    const sizes = new ResizeObserver(measure);
    sizes.observe(bar);
    measure();
    return () => {
      sizes.disconnect();
      cancelAnimationFrame(l.raf);
      l.raf = 0;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Вкладка сменилась — снаружи или нашим же выбором: круг едет к ней.
  useEffect(() => {
    if (!loop.current.slot || drag.current) return;
    lens.current.target = center(index);
    kick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  /** Координата пальца от левого края панели. */
  const barX = (clientX: number) => clientX - barRef.current!.getBoundingClientRect().left;

  const select = (i: number) => {
    lens.current.target = center(i);
    kick();
    if (ORDER[i] !== latest.current.active) {
      haptic();
      latest.current.onChange(ORDER[i]);
    } else {
      haptic("light");
      latest.current.onReselect?.(ORDER[i]);
    }
  };

  const endDrag = () => {
    drag.current = null;
    barRef.current?.classList.remove("tabbar__bar--dragging");
    lens.current.pressTarget = 0;
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    drag.current = { id: e.pointerId, startX: e.clientX, active: false, slot: index };
    lens.current.pressTarget = 1;
    kick();
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    if (!d.active) {
      if (Math.abs(e.clientX - d.startX) < DRAG_FROM) return;
      d.active = true;
      // Захват — чтобы палец, съехавший с панели, не бросил круг. Если браузер
      // откажет (указатель уже отпущен), жест всё равно доводим без него.
      try {
        barRef.current?.setPointerCapture(e.pointerId);
      } catch {
        /* без захвата */
      }
      // Пока круг в руке, подпись старой вкладки не висит над пустым местом.
      barRef.current?.classList.add("tabbar__bar--dragging");
    }
    const x = barX(e.clientX);
    lens.current.target = rubber(x, center(0), center(ORDER.length - 1));
    const slot = slotAt(x - INSET, loop.current.slot, ORDER.length);
    if (slot !== d.slot) {
      d.slot = slot;
      haptic();
    }
    kick();
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    endDrag();
    if (d.active) {
      // После перетаскивания браузер ещё пришлёт click — он уже не нужен.
      swallowClick.current = true;
      select(slotAt(barX(e.clientX) - INSET, loop.current.slot, ORDER.length));
    } else {
      kick();
    }
  };

  const onPointerCancel = () => {
    endDrag();
    lens.current.target = center(latest.current.index);
    kick();
  };

  return (
    <nav className="tabbar" aria-label="Разделы">
      <div
        ref={barRef}
        className="tabbar__bar"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onClickCapture={(e) => {
          if (!swallowClick.current) return;
          swallowClick.current = false;
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <span ref={lensRef} className="tabbar__indicator" aria-hidden="true" />
        {ORDER.map((tab, i) => {
          const on = active === tab;
          return (
            <button
              key={tab}
              className={`tab ${on ? "tab--active" : ""}`}
              aria-current={on ? "page" : undefined}
              aria-label={LABELS[tab]}
              onClick={() => select(i)}
            >
              <span className="tab__icon" ref={(el) => (iconRefs.current[i] = el)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                     strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  {ICONS[tab]}
                </svg>
                {/* Идущая тренировка видна из любого раздела: её нельзя потерять. */}
                {tab === "workout" && workoutActive && <span className="tab__pulse" />}
              </span>
              <span className="tab__label" aria-hidden="true">
                {LABELS[tab]}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
